import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetSceneContents,
  flattenSceneContents,
  useGetNextSceneContent
} from '@pocapan/shared/domains/scene/ui/hooks/useGetSceneContents';
import type { components } from '@pocapan/shared/types/schema';

type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];

interface UseScenePaginationListProps {
  sceneId: string | undefined;
  scenePreviewId: string;
  options?: {
    fallbackCursor?: string | null;
    disableBeforeMode?: boolean;
    deferToClickOnly?: boolean;
    contentsCount?: number | null;
    introContentsLength?: number;
    maxGroupSequenceNoFromIntro?: number;
  };
}

export const useScenePaginationList = ({
  sceneId,
  scenePreviewId,
  options
}: UseScenePaginationListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursorFromUrl = searchParams.get('cursor');
  const fallbackCursor = options?.fallbackCursor ?? null;
  const skipNextCursorResetRef = useRef(false);
  const prevSceneIdRef = useRef(sceneId);
  const prevCursorFromUrlRef = useRef(cursorFromUrl);

  const [effectiveCursor, setEffectiveCursor] = useState<string | null>(cursorFromUrl);
  const isBeforeMode = !options?.disableBeforeMode && effectiveCursor != null;
  const deferToClickOnly = options?.deferToClickOnly ?? false;
  const shouldFetchAfter = !deferToClickOnly;

  const {
    data,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isFetchingPreviousPage,
    isLoading: isBaseLoading
  } = useGetSceneContents(
    sceneId as string,
    scenePreviewId,
    isBeforeMode
      ? { cursor: effectiveCursor, direction: 'before', limit: 10 }
      : { direction: 'after', limit: 1, enabled: shouldFetchAfter }
  );

  const { mutateAsync: fetchNextOne, isLoading: isFetchingNextOne } = useGetNextSceneContent(
    sceneId as string,
    scenePreviewId,
    { limit: 1 }
  );

  const [forwardCursor, setForwardCursor] = useState<string | null>(
    cursorFromUrl ?? fallbackCursor
  );
  const [afterContents, setAfterContents] = useState<SceneContent[]>([]);
  const [hasNoMoreContent, setHasNoMoreContent] = useState(false);

  useEffect(() => {
    if (!sceneId) {
      setAfterContents([]);
      setForwardCursor(null);
      setEffectiveCursor(null);
      setHasNoMoreContent(false);
      return;
    }

    if (skipNextCursorResetRef.current) {
      skipNextCursorResetRef.current = false;
      prevSceneIdRef.current = sceneId;
      prevCursorFromUrlRef.current = cursorFromUrl;
      return;
    }

    const sceneIdChanged = prevSceneIdRef.current !== sceneId;
    const cursorFromUrlChanged = prevCursorFromUrlRef.current !== cursorFromUrl;
    prevSceneIdRef.current = sceneId;
    prevCursorFromUrlRef.current = cursorFromUrl;

    if (deferToClickOnly && !sceneIdChanged && !cursorFromUrlChanged) {
      // fallbackCursor(lastContentId) 변경 시 afterContents 유지 - 씬 완료 후 리셋 방지
      setForwardCursor((prev) => cursorFromUrl ?? fallbackCursor ?? prev);
      setEffectiveCursor(cursorFromUrl);
    } else {
      setAfterContents([]);
      setForwardCursor(cursorFromUrl ?? fallbackCursor);
      setEffectiveCursor(cursorFromUrl);
      setHasNoMoreContent(false);
    }
  }, [cursorFromUrl, fallbackCursor, sceneId, deferToClickOnly]);

  useEffect(() => {
    if (isBeforeMode) return;
    if (forwardCursor != null) return;

    const firstPage = data?.pages?.[0];
    const next = firstPage?.meta?.pagination?.nextCursor ?? null;
    if (next != null) setForwardCursor(next);
  }, [data?.pages, forwardCursor, isBeforeMode]);

  const baseContents = flattenSceneContents(data) ?? [];
  const contents = [...baseContents, ...afterContents];

  const contentsCount = options?.contentsCount ?? null;
  const totalCount = contentsCount ?? 0;
  const maxGroupSequenceNoFromIntro = options?.maxGroupSequenceNoFromIntro ?? 0;
  const maxGroupSequenceNoLoaded = Math.max(
    maxGroupSequenceNoFromIntro,
    ...contents.map((c) =>
      'sequenceGroupNo' in c &&
      typeof (c as { sequenceGroupNo?: number }).sequenceGroupNo === 'number'
        ? (c as { sequenceGroupNo: number }).sequenceGroupNo
        : ((c as { sequence?: number }).sequence ?? 0)
    )
  );
  const remainingPreviewsCount =
    contentsCount != null ? Math.max(0, contentsCount - maxGroupSequenceNoLoaded) : null;

  const loadMoreBefore = () => {
    if (!isBeforeMode) return;
    const hasMore = data?.pages?.[0]?.meta?.pagination?.hasMore;
    if (hasMore === false) return;
    if (isFetching || isFetchingPreviousPage || !hasPreviousPage) return;

    fetchPreviousPage();
  };

  const handleOpenNextPreview = async () => {
    if (!sceneId) return;
    if (isFetchingNextOne) return;
    if (hasNoMoreContent) return;
    const cursorToUse = forwardCursor ?? fallbackCursor;
    if (!cursorToUse) return;

    try {
      const res = await fetchNextOne({ cursor: cursorToUse });
      const newNextCursor = res?.meta?.pagination?.nextCursor ?? null;
      const newContents = (res?.sceneContents ?? []) as SceneContent[];

      if (newContents.length === 0 && newNextCursor == null) {
        setHasNoMoreContent(true);
      }

      setAfterContents((prev) => appendUniqueAfterContents(baseContents, prev, newContents));
      setForwardCursor(newNextCursor);

      if (newNextCursor) {
        skipNextCursorResetRef.current = true;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('cursor', newNextCursor);
            return next;
          },
          { replace: true }
        );
      }
    } catch {}
  };

  const isLoading = isBaseLoading;

  return {
    contents,
    isBeforeMode,
    isLoading,
    isFetchingPreviousPage,
    isFetchingNextOne,
    forwardCursor,
    hasNoMoreContent,
    loadMoreBefore,
    handleOpenNextPreview,
    baseContentsLength: baseContents.length,
    remainingPreviewsCount,
    totalCount,
    resetKey: `${sceneId ?? ''}-${effectiveCursor ?? ''}`
  };
};

export const getContentKey = (content: SceneContent): string => {
  const stableId = getStableContentId(content);
  if (stableId) return stableId;
  const seq =
    'sequence' in content ? content.sequence : (content as { sequenceNo?: number }).sequenceNo;
  if (typeof seq === 'number') return `seq-${seq}`;
  return 'content-unknown';
};

const getStableContentId = (content: SceneContent): string | null => {
  if ('sceneThreadId' in content && content.sceneThreadId) return content.sceneThreadId;
  if ('sceneChatId' in content && (content as { sceneChatId?: string }).sceneChatId) {
    return (content as { sceneChatId: string }).sceneChatId;
  }
  return null;
};

// 중복 제거 및 병합 로직
const appendUniqueAfterContents = (
  base: SceneContent[],
  afterPrev: SceneContent[],
  toAppend: SceneContent[]
): SceneContent[] => {
  const seen = new Set<string>();

  // 기존 base와 이미 로드된 after 데이터의 ID 수집
  for (const item of [...base, ...afterPrev]) {
    const id = getStableContentId(item);
    if (id) seen.add(id);
  }

  const next = [...afterPrev];
  for (const item of toAppend) {
    const id = getStableContentId(item);
    // 이미 존재하는 ID라면 건너뜀 (중복 방지)
    if (id && seen.has(id)) continue;

    if (id) seen.add(id);
    next.push(item);
  }

  return next;
};
